import { useTheme, type Theme } from '@mui/material/styles';
import type { TransitionProps } from '@mui/material/transitions';
import { cloneElement, forwardRef, useRef } from 'react';
import { Transition } from 'react-transition-group';

export type RotateProps = Omit<TransitionProps, 'children'> & {
  /**
   * The component used for the transition.
   * @default Transition
   */
  TransitionComponent?: typeof Transition; // NOTE: The prop is in PascalCase to match the original MUI's Transitions component
  children: React.ReactElement;
};

// NOTE: Return type is defined to match the style prop of the Transition component
function getTransitionStyle(theme: Theme): { [key: string]: React.CSSProperties } {
  return {
    entering: {
      transform: 'rotate(0deg)',
      opacity: 1,
      transition: `transform ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
    },
    entered: {
      transform: 'rotate(-180deg)',
      opacity: 1,
      transition: `transform ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
    },
    exiting: {
      transform: 'rotate(0deg)',
      opacity: 1,
      transition: `transform ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
    },
    exited: {
      transform: 'rotate(0deg)',
      opacity: 1,
      transition: `transform ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
    },
  };
}

/**
 * The Rotate transition can be used to rotate the component for 180 degree clockwise.
 * We can use this with the <ExpandMoreRoundedIcon /> to rotate the expand more icon.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 *
 * @example
 *
 * ```tsx
 * import { ExpandMoreRoundedIcon, Rotate } from '@shared/components';
 *
 * <Rotate in={isExpanded}>
 *  <ExpandMoreRoundedIcon />
 * </Rotate>
 *
 */
const Rotate = forwardRef<HTMLElement, RotateProps>((props: RotateProps, ref) => {
  const theme = useTheme();
  const nodeRef = useRef(null);

  const {
    appear = true,
    children,
    in: isDuringAnimation,
    timeout = {
      enter: theme.transitions.duration.enteringScreen,
      exit: theme.transitions.duration.leavingScreen,
    },
    TransitionComponent = Transition,
    ...restProps
  } = props;

  return (
    <TransitionComponent
      appear={appear}
      in={isDuringAnimation}
      nodeRef={nodeRef}
      timeout={timeout}
      {...restProps}
    >
      {(state, childProps) => {
        // NOTE: Ensure "ownerState" is not forwarded to the child DOM element when a direct HTML element is used. This avoids unexpected behavior since "ownerState" is intended for internal styling, component props and not as a DOM attribute. */}
        delete childProps?.ownerState;

        // NOTE: using cloneElement is uncommon and can lead to fragile code.
        // but we mimic how MUI implements the Transition component.
        return cloneElement(children, {
          ref,
          style: {
            ...getTransitionStyle(theme)[state],
            ...children.props.style,
          },
          ...childProps,
        });
      }}
    </TransitionComponent>
  );
});

Rotate.displayName = 'Rotate';

export { Rotate };
